import React, { useState, useEffect } from "react";
import "../../styles/report.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllCustomersRequest } from "../../redux/Customers/actions/customerActions";
import { fetchAllVendorsRequest } from "../../redux/Vendors/actions/vendorActions";
import {
  fetchAllInvoicesRequest,
  fetchCustomerInvoicesRequest,
  fetchVendorInvoicesRequest,
  resetInvoiceDetails,
} from "../../redux/Invoice/InvoiceAction";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { formatDateWithAmPm, getStatusClass } from "../../Style";
import {
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Button,
  TablePagination,
  Tabs,
  Tab,
  Paper,
  Typography,
  Divider,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  CalendarMonth,
  Download,
  Search,
  ArrowUpward,
  ArrowDownward,
  TrendingUp,
  AttachMoney,
  DateRange,
  CalendarToday,
  CalendarViewMonth,
  CalendarViewWeek,
  Money,
  MoneyOffCsredTwoTone,
  Settings,
  Person2,
} from "@mui/icons-material";
import DynamicYearSelector from "./DynamicYearSelector";
import { invoiceViewAllEffect } from "../../redux/Invoice/InvoiceEffect";

const ReportTable = ({
  type,
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  skipInitialApiCall = false,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [data, setData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [fullDataForExport, setFullDataForExport] = useState([]);
  const [searchColumn, setSearchColumn] = useState(
    type === "bookings" ? "booking_serial_no" : "name",
  );

  // Add sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  // Add tab state for sales report periods
  const [timePeriodTab, setTimePeriodTab] = useState(2); // 0: Daily, 1: Monthly, 2: Yearly

  // Add states for revenue metrics
  const [revenueMetrics, setRevenueMetrics] = useState({
    daily: {
      totalRevenue: 0,
      adminEarnings: 0,
      vendorAmount: 0,
      totalBookings: 0,
      totalServices: 0,
    },
    monthly: {
      totalRevenue: 0,
      adminEarnings: 0,
      vendorAmount: 0,
      totalBookings: 0,
      totalServices: 0,
    },
    yearly: {
      totalRevenue: 0,
      adminEarnings: 0,
      vendorAmount: 0,
      totalBookings: 0,
      totalServices: 0,
    },
  });

  // Add state for selected day (when daily tab is active)
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [filterType, setFilterType] = useState("all");
  const [selectedEntity, setSelectedEntity] = useState("");

  // Redux selectors
  const currentDate = new Date().toISOString().split("T")[0];
  const bookings = useSelector((state) => state?.Invoice?.invoices);
  const customerBookings = useSelector(
    (state) => state?.Invoice?.customerInvoice,
  );
  const vendorBookings = useSelector((state) => state?.Invoice?.vendorInvoice);
  const totalRecord = useSelector((state) => state.booking?.totalRecords || 0);
  const { loading: bookingLoading } = useSelector((state) => state.booking);

  const {
    customers: customerResponse,
    loading: customerLoading,
    error: customerError,
  } = useSelector((state) =>
    user?.user_type === 2 ? state.vendors : state.customers || [],
  );

  const {
    vendors: vendorResponse,
    loading: vendorLoading,
    error: vendorError,
  } = useSelector((state) => state.vendors || []);

  const loading =
    type === "customers"
      ? customerLoading
      : type === "vendors"
        ? vendorLoading
        : type === "bookings" || type === "sales" || type === "payments"
          ? bookingLoading
          : false;

  const error =
    type === "customers"
      ? customerError
      : type === "vendors"
        ? vendorError
        : null;

  const { customers = {} } = useSelector((state) => state.customers);
  const { vendors = {} } = useSelector((state) => state.vendors);
  const customerList = customers.data || [];
  const vendorList = vendors.data || [];

  const activeCustomers = Array.isArray(customerList)
    ? customerList.filter((customer) => customer?.status === 1)
    : [];

  const activeVendors = Array.isArray(vendorList)
    ? vendorList.filter((vendor) => vendor?.status === 1)
    : [];

  const customerOptions = activeCustomers.map((customer) => ({
    value: customer.id,
    label: `${customer.display_name} - ${customer.customer_serial_no}`,
  }));

  const vendorOptions = activeVendors.map((vendor) => ({
    value: vendor.id,
    label: vendor.agency_name,
  }));

  const getMonthName = (monthNumber) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[monthNumber - 1];
  };

  // Function to format date for API calls
  const formatDateForAPI = (year, month, day = 1) => {
    const formattedMonth = String(month).padStart(2, "0");
    const formattedDay = String(day).padStart(2, "0");
    return `${year}-${formattedMonth}-${formattedDay}`;
  };

  // Function to get API parameters based on time period and report type
  const getApiParameters = () => {
    let params = {
      pageNo: page + 1,
      pagesize: rowsPerPage,
      pagination_required: true,
    };

    if (searchQuery) {
      params.search = searchQuery;
      if (searchColumn) {
        params.search_by = searchColumn;
      }
    }

    // Handle different report types
    if (type === "sales") {
      if (timePeriodTab === 0) {
        // Daily
        params.type = "daily";
        params.date_from = formatDateForAPI(
          selectedYear,
          selectedMonth,
          selectedDay,
        );
      } else if (timePeriodTab === 1) {
        // Monthly
        params.type = "monthly";
        params.date_from = formatDateForAPI(selectedYear, selectedMonth);
      } else {
        // Yearly
        params.type = "yearly";
        params.date_from = formatDateForAPI(selectedYear, 1);
      }
    } else if (type === "payments" || type === "bookings") {
      // Always use monthly filter for payments and bookings
      params.type = "monthly";
      params.date_from = formatDateForAPI(selectedYear, selectedMonth);
    }

    return params;
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      dispatch(resetInvoiceDetails());
    };
  }, []);

  // Load customers and vendors data once - FIXED: Only load when needed
  useEffect(() => {
    if (!skipInitialApiCall) {
      if (
        type === "customers" ||
        type === "sales" ||
        type === "bookings" ||
        type === "payments"
      ) {
        dispatch(fetchAllCustomersRequest());
      }
      if (
        type === "vendors" ||
        type === "sales" ||
        type === "bookings" ||
        type === "payments"
      ) {
        dispatch(fetchAllVendorsRequest());
      }
    }
  }, [dispatch, skipInitialApiCall, type]);

  // Add a reset function for when changing time periods
  const resetDataAndMetrics = () => {
    setData([]);
    setFullDataForExport([]);
    setRevenueMetrics({
      daily: {
        totalRevenue: 0,
        adminEarnings: 0,
        vendorAmount: 0,
        totalBookings: 0,
        totalServices: 0,
      },
      monthly: {
        totalRevenue: 0,
        adminEarnings: 0,
        vendorAmount: 0,
        totalBookings: 0,
        totalServices: 0,
      },
      yearly: {
        totalRevenue: 0,
        adminEarnings: 0,
        vendorAmount: 0,
        totalBookings: 0,
        totalServices: 0,
      },
    });
  };

  // FIXED: Create a separate function to make API calls
  const makeApiCall = () => {
    if (skipInitialApiCall && (type === "customers" || type === "vendors")) {
      return;
    }

    if (type === "bookings" || type === "sales" || type === "payments") {
      resetDataAndMetrics();
      dispatch(resetInvoiceDetails());

      const apiParams = getApiParameters();

      if (
        filterType === "all" ||
        filterType === "instant" ||
        filterType === "complementary"
      ) {
        dispatch(fetchAllInvoicesRequest(apiParams));
      } else if (filterType === "customer" && selectedEntity) {
        dispatch(
          fetchCustomerInvoicesRequest({
            ...apiParams,
            customerId: selectedEntity,
          }),
        );
      } else if (filterType === "vendor" && selectedEntity) {
        dispatch(
          fetchVendorInvoicesRequest({
            ...apiParams,
            vendorId: selectedEntity,
          }),
        );
      }
    }
  };

  // FIXED: Separate useEffect for different triggers to avoid multiple API calls
  useEffect(() => {
    // Only trigger for filter changes, entity changes, pagination, and search
    makeApiCall();
  }, [filterType, selectedEntity, page, rowsPerPage, searchQuery]);

  // FIXED: Separate useEffect for time period changes
  useEffect(() => {
    // Only trigger for time-related changes
    if (type === "sales" || type === "bookings" || type === "payments") {
      makeApiCall();
    }
  }, [timePeriodTab, selectedMonth, selectedYear, selectedDay]);

  // FIXED: Initial load effect
  useEffect(() => {
    // Only make initial API call once when component mounts
    if (
      !skipInitialApiCall ||
      type === "sales" ||
      type === "bookings" ||
      type === "payments"
    ) {
      makeApiCall();
    }
  }, [dispatch, type]); // Only depend on dispatch and type for initial load

  // Process and transform data from Redux store
  useEffect(() => {
    let transformedData = [];
    let fullExportData = [];

    // Use the API response data directly for sales, bookings and payments
    if (type === "sales" || type === "bookings" || type === "payments") {
      let apiData = [];
      let currentBookingData = null;

      if (filterType === "all") {
        apiData = Array.isArray(bookings?.data) ? bookings?.data : [];
        currentBookingData = bookings;
      } else if (filterType === "customer" && selectedEntity) {
        apiData = Array.isArray(customerBookings?.data)
          ? customerBookings?.data
          : [];
        currentBookingData = customerBookings;
      } else if (filterType === "vendor" && selectedEntity) {
        apiData = Array.isArray(vendorBookings?.data)
          ? vendorBookings?.data
          : [];
        currentBookingData = vendorBookings;
      }

      // Update revenue metrics from API response - handle empty data properly (only for sales)
      if (type === "sales") {
        const revenueData = currentBookingData || {};

        // Only update metrics if we have a valid API response
        const hasValidResponse =
          currentBookingData &&
          (currentBookingData.hasOwnProperty("totalPaidAmount") ||
            currentBookingData.hasOwnProperty("totalRecords") ||
            currentBookingData.hasOwnProperty("data"));

        if (hasValidResponse) {
          const currentMetrics = {
            totalRevenue: revenueData.totalPaidAmount || 0,
            adminEarnings:
              (revenueData.totalPaidAmount || 0) -
              (revenueData.totalVendorAmount || 0),
            vendorAmount: revenueData.totalVendorAmount || 0,
            totalBookings: revenueData.totalRecords || 0,
            totalServices: revenueData.totalNumberOfServices || 0,
          };

          // Only update the current time period metrics
          setRevenueMetrics((prev) => ({
            ...prev,
            [timePeriodTab === 0
              ? "daily"
              : timePeriodTab === 1
                ? "monthly"
                : "yearly"]: currentMetrics,
          }));
        }
      }

      // Only process data if we have a valid response with data
      const hasValidResponse =
        currentBookingData &&
        (currentBookingData.hasOwnProperty("totalPaidAmount") ||
          currentBookingData.hasOwnProperty("totalRecords") ||
          currentBookingData.hasOwnProperty("data"));

      if (!hasValidResponse || !apiData || apiData.length === 0) {
        setData([]);
        setFullDataForExport([]);
        return;
      }

      if (type === "sales") {
        transformedData = apiData.map((item) => {
          const order = item.Order || {};
          const customer = item.Customer || {};
          const paymentLogs = item?.paymentLogs || [];
          const vendorBookLogs = item.vendorBookLogs || [];
          const service = item.Service || {};
          const bookingDetail = item.BookingDetail || {};
          const bookingFamilies = item.bookingFamilies || [];
          console.log("item", item);
          console.log("vendorBookLogs", vendorBookLogs);
          console.log("paymentLogs", paymentLogs);
          console.log("order", order);
          const vendorTotal =
            vendorBookLogs.length > 0 ? vendorBookLogs[0].total || 0 : 0;
          const vendorName =
            vendorBookLogs.length > 0 && vendorBookLogs[0].vendor_id
              ? vendorList.find((v) => v.id === vendorBookLogs[0].vendor_id)
                  ?.agency_name || "-"
              : "-";

          const totalRevenue =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].price || 0
              : order?.sub_total;

          const adminProfit =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].total || 0
              : order?.sub_total;
          const vendorCommission =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].vendor_commision || 0
              : 0;

          // Calculate child discount from booking families
          const childDiscount = bookingFamilies
            .filter(
              (family) =>
                family.familyMember &&
                family.familyMember.family_type === "Childs",
            )
            .reduce(
              (sum, family) => sum + (family.familyMember.discount || 0),
              0,
            );

          let paymentStatusText = "Pending";
          if (item.payment_status === 1) {
            paymentStatusText = "Paid";
          } else if (item.payment_status === 2) {
            paymentStatusText = "Failed";
          } else if (item.payment_status === 3) {
            paymentStatusText = "Refunded";
          } else if (item.payment_status === 4) {
            paymentStatusText = "Aborted";
          }

          return {
            booking_serial_no: item.booking_serial_no || "-",
            invoice_date: item.created_at
              ? formatDateWithAmPm(item.created_at)
              : "-",
            customer_name: customer?.full_name || "-",
            vendor_name: vendorName,
            name: item.Airport?.name || "-",
            igst: order.tax > 0 ? order.tax : 0,
            cgst:
              vendorBookLogs.length > 0
                ? vendorBookLogs[0].cgst || 0
                : order.cgst || 0,
            sgst:
              vendorBookLogs.length > 0
                ? vendorBookLogs[0].sgst || 0
                : order.sgst || 0,
            sub_total:
              vendorBookLogs.length > 0
                ? vendorBookLogs[0].sub_total || 0
                : order.sub_total || 0,
            discount: order.discount || 0,
            child_discount: childDiscount > 0 ? `${childDiscount}%` : "-",
            service: service?.services || "-",
            numberOfService: bookingDetail?.number_of_service || "-",
            total_revenue: totalRevenue.toFixed(2) || "0.00",
            vendor_commission: vendorCommission?.toFixed(2) || "0.00",
            vendor_amount: vendorTotal?.toFixed(2) || "0.00",
            admin_earnings: adminProfit?.toFixed(2) || "0.00",
            sales_status: paymentStatusText,
            gross_total: totalRevenue?.toFixed(2) || "0.00",
            booking_type:
              item.booked_type === 1
                ? "Regular"
                : item.booked_type === 2
                  ? "Complementary"
                  : item.booked_type === 3
                    ? "Instant"
                    : "-",
          };
        });
      } else if (type === "bookings") {
        transformedData = apiData.map((item) => {
          const bookingDetail = item.BookingDetail || {};
          const billingAddress = item.BillingAddress || {};
          const order = item.Order || {};
          const bookingFamilies = item.bookingFamilies || [];
          const numberOfAdults = bookingDetail.number_of_adults || 0;
          const numberOfChildren = bookingDetail.number_of_child || 0;
          const numberOfInfants = bookingDetail.number_of_infant || 0;
          const totalPassengers =
            numberOfAdults + numberOfChildren + numberOfInfants;

          // Calculate child discount from booking families
          const childDiscount = bookingFamilies
            .filter(
              (family) =>
                family.familyMember &&
                family.familyMember.family_type === "Childs",
            )
            .reduce(
              (sum, family) => sum + (family.familyMember.discount || 0),
              0,
            );

          const bookedUser = bookingDetail["BookedUser"] || {};
          let bookedUserName = "-";
          if (bookedUser?.user_type === 3 && bookedUser?.Customer) {
            bookedUserName = bookedUser?.Customer?.full_name;
          } else if (bookedUser?.user_type === 2 && bookedUser?.Vendor) {
            bookedUserName = bookedUser?.Vendor?.full_name;
          } else if (bookedUser?.user_type === 4 && bookedUser?.Staff) {
            bookedUserName = bookedUser?.Staff?.full_name;
          } else {
            bookedUserName =
              `${bookedUser?.first_name || ""} ${
                bookedUser?.last_name || ""
              }`.trim() || "-";
          }

          return {
            booking_number: item.id || "-",
            booking_serial_no: item.booking_serial_no || "-",
            date_of_booking: item.created_at
              ? formatDateWithAmPm(item.created_at)
              : "-",
            booked_user_name: bookedUserName || "-",
            booked_for: item?.Customer?.full_name || "-",
            pnr_number: item?.pnr_number || "-",
            airport_name: item.airport_name || "-",
            airport_type: item.airport_type || "-",
            first_name: billingAddress.first_name || "-",
            last_name: billingAddress.last_name || "-",
            company_name: billingAddress.company_name || "-",
            number_of_adults: numberOfAdults || "-",
            number_of_child: numberOfChildren || "-",
            number_of_infant: numberOfInfants || "-",
            total_passenger: totalPassengers,
            child_discount: childDiscount > 0 ? `${childDiscount}%` : "-",
            service: item.Service?.services || order.service || "-",
            service_type_label:
              { 1: "Arrival", 2: "Departure", 3: "Transit" }[
                item.service_type
              ] || "-",
            notes: bookingDetail.notes || "-",
            datetime_of_arrival:
              bookingDetail?.date_of_arrival !== null
                ? `${bookingDetail?.date_of_arrival} ${bookingDetail?.time_of_arrival}`
                : "-",
            datetime_of_departure:
              bookingDetail?.date_of_departure !== null
                ? `${bookingDetail?.date_of_departure} ${bookingDetail?.time_of_departure}`
                : "-",
            arrival_city: bookingDetail.arrival_city || "-",
            arrival_airline: bookingDetail.arrival_airlines || "-",
            arrival_flight_number: bookingDetail.arrival_flight_number || "-",
            departure_city: bookingDetail.departure_city || "-",
            departure_airline: bookingDetail.departure_airlines || "-",
            departure_flight_number:
              bookingDetail.departure_flight_number || "-",
            payment_status: order.status || "-",
            status: item.status || 0,
            arrival_flight:
              `${bookingDetail.arrival_airlines || ""} ${
                bookingDetail.arrival_flight_number || ""
              }`.trim() || "-",
            departure_flight:
              `${bookingDetail.departure_airlines || ""} ${
                bookingDetail.departure_flight_number || ""
              }`.trim() || "-",
            booked_type: item.booked_type || 1,
          };
        });
      } else if (type === "payments") {
        transformedData = apiData.map((item) => {
          const order = item.Order || {};
          const customer = item.Customer || {};
          const billingAddress = item.BillingAddress || {};
          const bookingDetail = item.BookingDetail || {};

          // Calculate payment status text
          let paymentStatusText = "Pending";
          if (item.payment_status === 1) {
            paymentStatusText = "Paid";
          } else if (item.payment_status === 2) {
            paymentStatusText = "Failed";
          } else if (item.payment_status === 3) {
            paymentStatusText = "Refunded";
          } else if (item.payment_status === 4) {
            paymentStatusText = "Aborted";
          }

          const paymentLogs = item.paymentLogs || [];
          const paymentRef =
            paymentLogs.length > 0 ? paymentLogs[0].booking_refernce_no : "-";

          const vendorBookLogs = item.vendorBookLogs || [];
          const vendorCommission =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].vendor_commision || 0
              : 0;
          const grossTotal =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].price || 0
              : order?.sub_total || 0;
          const finalSubTotal =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].sub_total || 0
              : order?.sub_total || 0;
          const finalTotalAmount =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].total || 0
              : order?.total || 0;
          const cgstValue =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].cgst || 0
              : order?.cgst || 0;
          const sgstValue =
            vendorBookLogs.length > 0
              ? vendorBookLogs[0].sgst || 0
              : order?.sgst || 0;

          return {
            booking_serial_no: item.booking_serial_no || "-",
            transaction_id: paymentRef,
            payment_date: item.created_at
              ? formatDateWithAmPm(item.created_at)
              : "-",
            customer_name: customer?.full_name || "-",
            service: item.Service?.services || "-",
            service_date:
              bookingDetail?.date_of_arrival ||
              bookingDetail?.date_of_departure ||
              "-",
            gross_total: grossTotal?.toFixed(2) || "0.00",
            vendor_discount: vendorCommission?.toFixed(2) || "0.00",
            sub_total: finalSubTotal?.toFixed(2) || "0.00",
            cgst: cgstValue?.toFixed(2) || "0.00",
            sgst: sgstValue?.toFixed(2) || "0.00",
            discount: order?.discount || "0",
            total_amount: finalTotalAmount?.toFixed(2) || "0.00",
            payment_status: paymentStatusText,
            pnr_number: item.pnr_number || "-",
          };
        });
      }

      setData(transformedData);
      setFullDataForExport(transformedData);
      return;
    }

    // Filter function using UTC methods (for other report types)
    const filterByMonthAndYear = (data, dateField) => {
      return data.filter((item) => {
        if (!item[dateField]) return false;

        const itemDate = new Date(item[dateField]);
        // Use UTC methods to avoid timezone conversion issues
        const itemMonth = itemDate.getUTCMonth() + 1;
        const itemYear = itemDate.getUTCFullYear();

        return itemMonth === selectedMonth && itemYear === selectedYear;
      });
    };

    switch (type) {
      case "customers":
        if (customerResponse?.data) {
          const filteredCustomers = filterByMonthAndYear(
            customerResponse.data,
            "created_at",
          );

          transformedData = filteredCustomers.map((customer) => ({
            name: `${customer.first_name || ""} ${
              customer.last_name || ""
            }`.trim(),
            email: customer.email || "-",
            phone: customer.phone || "-",
            address: customer.address1
              ? `${customer.address1}, ${customer.city_name || ""}, ${
                  customer.state_name || ""
                }, ${customer.country_name || ""}, ${customer.pincode || ""}`
              : "-",
            lastActive: customer.last_active,
            dateAdded: customer.created_at,
            booking_number: customer.booking_serial_no || "-",
            booking_status: customer.status === 0 ? "Active" : "Inactive",
          }));

          setData(transformedData);
          setFullDataForExport(transformedData);
        }
        break;

      case "vendors":
        if (vendorResponse?.data) {
          const filteredVendors = filterByMonthAndYear(
            vendorResponse.data,
            "created_at",
          );

          transformedData = filteredVendors.map((vendor) => ({
            agencyName: vendor.agency_name || "-",
            contactPerson: `${vendor.first_name || ""} ${
              vendor.last_name || ""
            }`.trim(),
            email: vendor.email || "-",
            phone: vendor.phone || "-",
            address: vendor.address1
              ? `${vendor.address1}, ${vendor.city_name || ""}, ${
                  vendor.state_name || ""
                }, ${vendor.country_name || ""}, ${vendor.pincode || ""}`
              : "-",
            registrationNumber: vendor.iata_reg_no || "-",
            gstin: vendor.gstin_number || "-",
            pan: vendor.pan_number || "-",
            lastActive: vendor.last_active,
            createdDate: vendor.created_at,
            booking_status: vendor.status === 0 ? "Active" : "Inactive",
            approvalStatus:
              vendor.approove_status === 1
                ? "Approved"
                : vendor.approove_status === 2
                  ? "Pending"
                  : "Rejected",
          }));

          setData(transformedData);
          setFullDataForExport(transformedData);
        }
        break;
    }
  }, [
    bookings,
    customerBookings,
    vendorBookings,
    type,
    filterType,
    selectedEntity,
    selectedMonth,
    selectedYear,
    selectedDay,
    timePeriodTab,
    vendorList,
    customerResponse,
    vendorResponse,
  ]);

  // Sort function for table data
  const sortData = (data, key, direction) => {
    if (!key || key === null) return data;

    return [...data].sort((a, b) => {
      if (
        key.includes("date") ||
        key === "createdDate" ||
        key === "lastActive" ||
        key === "dateAdded" ||
        key === "datetime_of_arrival" ||
        key === "datetime_of_departure"
      ) {
        const dateA = a[key] === "-" ? new Date(0) : new Date(a[key]);
        const dateB = b[key] === "-" ? new Date(0) : new Date(b[key]);

        return direction === "ascending" ? dateA - dateB : dateB - dateA;
      }

      if (typeof a[key] === "number" && typeof b[key] === "number") {
        return direction === "ascending" ? a[key] - b[key] : b[key] - a[key];
      }

      const valueA = a[key]?.toString().toLowerCase() || "";
      const valueB = b[key]?.toString().toLowerCase() || "";

      if (valueA < valueB) {
        return direction === "ascending" ? -1 : 1;
      }
      if (valueA > valueB) {
        return direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortDirection = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction;
  };

  const filteredData = React.useMemo(() => {
    let result = [...data];

    if (type === "sales" || type === "bookings" || type === "payments") {
      if (sortConfig.key) {
        result = sortData(result, sortConfig.key, sortConfig.direction);
      }
      return result;
    }

    if (searchQuery) {
      const searchTermLower = searchQuery.toLowerCase();
      result = result.filter((item) => {
        switch (type) {
          case "customers":
            return (
              item.name?.toLowerCase().includes(searchTermLower) ||
              item.email?.toLowerCase().includes(searchTermLower) ||
              item.phone?.toLowerCase().includes(searchTermLower)
            );
          case "vendors":
            return (
              item.agencyName?.toLowerCase().includes(searchTermLower) ||
              item.contactPerson?.toLowerCase().includes(searchTermLower) ||
              item.email?.toLowerCase().includes(searchTermLower) ||
              item.phone?.toLowerCase().includes(searchTermLower)
            );
          default:
            return true;
        }
      });
    }

    if (sortConfig.key) {
      result = sortData(result, sortConfig.key, sortConfig.direction);
    }

    return result;
  }, [data, searchQuery, type, sortConfig]);

  const paginatedData = React.useMemo(() => {
    if (type === "sales" || type === "bookings" || type === "payments") {
      return filteredData;
    }

    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage, type]);

  useEffect(() => {
    if (type !== "sales" && type !== "bookings" && type !== "payments") {
      setPage(0);
    }
  }, [searchQuery, data, sortConfig, type]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const renderSortableHeader = (label, key) => {
    const direction = getSortDirection(key);
    return (
      <th
        onClick={() => requestSort(key)}
        style={{ cursor: "pointer" }}
        className="sortable-header"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {label}
          <span style={{ marginLeft: "5px" }}>
            {direction === "ascending" ? (
              <ArrowUpward fontSize="small" />
            ) : direction === "descending" ? (
              <ArrowDownward fontSize="small" />
            ) : null}
          </span>
        </div>
      </th>
    );
  };

  const handleTimePeriodChange = (event, newValue) => {
    setTimePeriodTab(newValue);
    setPage(0);
    resetDataAndMetrics();
    dispatch(resetInvoiceDetails());
  };

  const getCurrentMetrics = () => {
    switch (timePeriodTab) {
      case 0:
        return revenueMetrics.daily;
      case 1:
        return revenueMetrics.monthly;
      case 2:
        return revenueMetrics.yearly;
      default:
        return revenueMetrics.monthly;
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const handleDayChange = (event) => {
    setSelectedDay(Number(event.target.value));
    setPage(0);
    resetDataAndMetrics();
    dispatch(resetInvoiceDetails());
  };

  const handleMonthChange = (month) => {
    if (type === "sales" && timePeriodTab === 1) {
      setSelectedMonth(month);
      setPage(0);
      resetDataAndMetrics();
      dispatch(resetInvoiceDetails());
    } else if (type === "payments" || type === "bookings") {
      setSelectedMonth(month);
      setPage(0);
      resetDataAndMetrics();
      dispatch(resetInvoiceDetails());
    } else {
      setSelectedMonth(month);
      setPage(0);
    }
  };
  const handleYearChange = (year) => {
    setSelectedYear(year);
    setPage(0);
    if (type === "sales" || type === "payments" || type === "bookings") {
      resetDataAndMetrics();
      dispatch(resetInvoiceDetails());
    }
  };

  // Modified handleExport function - make direct API call instead of using Redux
  const handleExport = async () => {
    try {
      setExportLoading(true);
      let exportData = [];

      // For sales, bookings, and payments - make direct API call without affecting Redux state
      if (type === "sales" || type === "bookings" || type === "payments") {
        // Prepare API parameters for export (without pagination)
        let params = {
          pagination_required: false, // Key: Only for export
        };

        if (searchQuery) {
          params.search = searchQuery;
          if (searchColumn) {
            params.search_by = searchColumn;
          }
        }

        // Handle different report types
        if (type === "sales") {
          if (timePeriodTab === 0) {
            // Daily
            params.type = "daily";
            params.date_from = formatDateForAPI(
              selectedYear,
              selectedMonth,
              selectedDay,
            );
          } else if (timePeriodTab === 1) {
            // Monthly
            params.type = "monthly";
            params.date_from = formatDateForAPI(selectedYear, selectedMonth);
          } else {
            // Yearly
            params.type = "yearly";
            params.date_from = formatDateForAPI(selectedYear, 1);
          }
        } else if (type === "payments" || type === "bookings") {
          // Always use monthly filter for payments and bookings
          params.type = "monthly";
          params.date_from = formatDateForAPI(selectedYear, selectedMonth);
        }

        // Add customer/vendor specific parameters
        if (filterType === "customer" && selectedEntity) {
          params.customer_id = selectedEntity;
        } else if (filterType === "vendor" && selectedEntity) {
          params.vendor_id = selectedEntity;
        }

        // Make direct API call without using Redux
        try {
          const response = await invoiceViewAllEffect(params); // Direct API call
          const apiData = Array.isArray(response?.data) ? response.data : [];

          if (!apiData || apiData.length === 0) {
            toast.error("No data available for export");
            return;
          }

          // Transform the data based on report type (same transformation logic as before)
          if (type === "payments") {
            exportData = apiData.map((item) => {
              const order = item.Order || {};
              const customer = item.Customer || {};
              const bookingDetail = item.BookingDetail || {};

              let paymentStatusText = "Pending";
              if (item.payment_status === 1) {
                paymentStatusText = "Paid";
              } else if (item.payment_status === 2) {
                paymentStatusText = "Failed";
              } else if (item.payment_status === 3) {
                paymentStatusText = "Refunded";
              } else if (item.payment_status === 4) {
                paymentStatusText = "Aborted";
              }

              const paymentLogs = item.paymentLogs || [];
              const paymentRef =
                paymentLogs.length > 0
                  ? paymentLogs[0].booking_refernce_no
                  : "-";

              const vendorBookLogs = item.vendorBookLogs || [];
              const vendorCommission =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].vendor_commision || 0
                  : 0;
              const grossTotal =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].price || 0
                  : order?.sub_total || 0;
              const finalSubTotal =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].sub_total || 0
                  : order?.sub_total || 0;
              const finalTotalAmount =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].total || 0
                  : order?.total || 0;
              const cgstValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].cgst || 0
                  : order?.cgst || 0;
              const sgstValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].sgst || 0
                  : order?.sgst || 0;

              return {
                booking_serial_no: item.booking_serial_no || "-",
                transaction_id: paymentRef,
                payment_date: item.created_at
                  ? formatDateWithAmPm(item.created_at)
                  : "-",
                customer_name: customer?.full_name || "-",
                service: item.Service?.services || "-",
                service_date:
                  bookingDetail?.date_of_arrival ||
                  bookingDetail?.date_of_departure ||
                  "-",
                gross_total: grossTotal?.toFixed(2) || "0.00",
                vendor_discount: vendorCommission?.toFixed(2) || "0.00",
                sub_total: finalSubTotal?.toFixed(2) || "0.00",
                cgst: cgstValue?.toFixed(2) || "0.00",
                sgst: sgstValue?.toFixed(2) || "0.00",
                discount: order?.discount || "0",
                total_amount: finalTotalAmount?.toFixed(2) || "0.00",
                payment_status: paymentStatusText,
                pnr_number: item.pnr_number || "-",
              };
            });
          } else if (type === "sales") {
            exportData = apiData.map((item) => {
              const order = item.Order || {};
              const customer = item.Customer || {};
              const paymentLogs = item?.paymentLogs || [];
              const vendorBookLogs = item.vendorBookLogs || [];
              const service = item.Service || {};
              const bookingDetail = item.BookingDetail || {};
              const bookingFamilies = item.bookingFamilies || [];

              const vendorName =
                vendorBookLogs.length > 0 && vendorBookLogs[0].vendor_id
                  ? vendorList.find((v) => v.id === vendorBookLogs[0].vendor_id)
                      ?.agency_name || "-"
                  : "-";

              const totalRevenueValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].price || 0
                  : order?.sub_total;

              const adminProfitValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].total || 0
                  : order?.sub_total;

              const vendorCommissionValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].vendor_commision || 0
                  : 0;

              const childDiscount = bookingFamilies
                .filter(
                  (family) =>
                    family.familyMember &&
                    family.familyMember.family_type === "Childs",
                )
                .reduce(
                  (sum, family) => sum + (family.familyMember.discount || 0),
                  0,
                );

              let paymentStatusText = "Pending";
              if (item.payment_status === 1) {
                paymentStatusText = "Paid";
              } else if (item.payment_status === 2) {
                paymentStatusText = "Failed";
              } else if (item.payment_status === 3) {
                paymentStatusText = "Refunded";
              } else if (item.payment_status === 4) {
                paymentStatusText = "Aborted";
              }

              const subTotalValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].sub_total || 0
                  : order.sub_total || 0;
              const cgstValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].cgst || 0
                  : order.cgst || 0;
              const sgstValue =
                vendorBookLogs.length > 0
                  ? vendorBookLogs[0].sgst || 0
                  : order.sgst || 0;

              return {
                booking_serial_no: item.booking_serial_no || "-",
                invoice_date: item.created_at
                  ? formatDateWithAmPm(item.created_at)
                  : "-",
                customer_name: customer?.full_name || "-",
                vendor_name: vendorName,
                name: item.Airport?.name || "-",
                gross_total: totalRevenueValue?.toFixed(2) || "0.00",
                vendor_commission: vendorCommissionValue?.toFixed(2) || "0.00",
                sub_total: subTotalValue?.toFixed(2) || "0.00",
                cgst: cgstValue?.toFixed(2) || "0.00",
                sgst: sgstValue?.toFixed(2) || "0.00",
                igst: order.tax > 0 ? order.tax : 0,
                discount: order.discount || 0,
                child_discount: childDiscount > 0 ? `${childDiscount}%` : "-",
                service: service?.services || "-",
                numberOfService: bookingDetail?.number_of_service || "-",
                admin_earnings: adminProfitValue?.toFixed(2) || "0.00",
                sales_status: paymentStatusText,
                booking_type:
                  item.booked_type === 1
                    ? "Regular"
                    : item.booked_type === 2
                      ? "Complementary"
                      : item.booked_type === 3
                        ? "Instant"
                        : "-",
              };
            });
          } else if (type === "bookings") {
            exportData = apiData.map((item) => {
              const bookingDetail = item.BookingDetail || {};
              const billingAddress = item.BillingAddress || {};
              const order = item.Order || {};
              const bookingFamilies = item.bookingFamilies || [];
              const numberOfAdults = bookingDetail.number_of_adults || 0;
              const numberOfChildren = bookingDetail.number_of_child || 0;
              const numberOfInfants = bookingDetail.number_of_infant || 0;
              const totalPassengers =
                numberOfAdults + numberOfChildren + numberOfInfants;

              const childDiscount = bookingFamilies
                .filter(
                  (family) =>
                    family.familyMember &&
                    family.familyMember.family_type === "Childs",
                )
                .reduce(
                  (sum, family) => sum + (family.familyMember.discount || 0),
                  0,
                );

              const bookedUser = bookingDetail["BookedUser"] || {};
              let bookedUserName = "-";
              if (bookedUser?.user_type === 3 && bookedUser?.Customer) {
                bookedUserName = bookedUser?.Customer?.full_name;
              } else if (bookedUser?.user_type === 2 && bookedUser?.Vendor) {
                bookedUserName = bookedUser?.Vendor?.full_name;
              } else if (bookedUser?.user_type === 4 && bookedUser?.Staff) {
                bookedUserName = bookedUser?.Staff?.full_name;
              } else {
                bookedUserName =
                  `${bookedUser?.first_name || ""} ${
                    bookedUser?.last_name || ""
                  }`.trim() || "-";
              }

              return {
                booking_number: item.id || "-",
                booking_serial_no: item.booking_serial_no || "-",
                date_of_booking: item.created_at
                  ? formatDateWithAmPm(item.created_at)
                  : "-",
                booked_user_name: bookedUserName || "-",
                booked_for: item?.Customer?.full_name || "-",
                pnr_number: item?.pnr_number || "-",
                airport_name: item.airport_name || "-",
                airport_type: item.airport_type || "-",
                first_name: billingAddress.first_name || "-",
                last_name: billingAddress.last_name || "-",
                company_name: billingAddress.company_name || "-",
                number_of_adults: numberOfAdults || "-",
                number_of_child: numberOfChildren || "-",
                number_of_infant: numberOfInfants || "-",
                total_passenger: totalPassengers,
                child_discount: childDiscount > 0 ? `${childDiscount}%` : "-",
                service: item.Service?.services || order.service || "-",
                service_type_label:
                  { 1: "Arrival", 2: "Departure", 3: "Transit" }[
                    item.service_type
                  ] || "-",
                notes: bookingDetail.notes || "-",
                datetime_of_arrival:
                  bookingDetail?.date_of_arrival !== null
                    ? `${bookingDetail?.date_of_arrival} ${bookingDetail?.time_of_arrival}`
                    : "-",
                datetime_of_departure:
                  bookingDetail?.date_of_departure !== null
                    ? `${bookingDetail?.date_of_departure} ${bookingDetail?.time_of_departure}`
                    : "-",
                arrival_city: bookingDetail.arrival_city || "-",
                arrival_airline: bookingDetail.arrival_airlines || "-",
                arrival_flight_number:
                  bookingDetail.arrival_flight_number || "-",
                departure_city: bookingDetail.departure_city || "-",
                departure_airline: bookingDetail.departure_airlines || "-",
                departure_flight_number:
                  bookingDetail.departure_flight_number || "-",
                payment_status: order.status || "-",
                status: item.status || 0,
                arrival_flight:
                  `${bookingDetail.arrival_airlines || ""} ${
                    bookingDetail.arrival_flight_number || ""
                  }`.trim() || "-",
                departure_flight:
                  `${bookingDetail.departure_airlines || ""} ${
                    bookingDetail.departure_flight_number || ""
                  }`.trim() || "-",
                booked_type: item.booked_type || 1,
              };
            });
          }
        } catch (apiError) {
          console.error("Export API error:", apiError);
          toast.error("Failed to fetch export data");
          return;
        }
      } else {
        // For customers and vendors, use existing fullDataForExport
        exportData = fullDataForExport;
      }

      // Generate CSV content (same logic as before)
      let csvContent = "";
      let filename = "";

      if (type === "payments") {
        const headers = [
          "Booking Number",
          "Transaction ID",
          "Payment Date",
          "Customer Name",
          "Service",
          "Service Date",
          "Gross Total (incl discount %)",
          "Vendor Discount Amount",
          "Sub Total",
          "CGST",
          "SGST",
          "Discount",
          "Total Amount",
          "Payment Status",
          "Payment Type",
          "PNR Number",
        ];

        csvContent += headers.join(",") + "\n";

        exportData.forEach((payment) => {
          const row = [
            payment.booking_serial_no,
            payment.transaction_id,
            payment.payment_date,
            payment.customer_name,
            payment.service,
            payment.service_date,
            payment.gross_total,
            payment.vendor_discount,
            payment.sub_total,
            payment.cgst,
            payment.sgst,
            payment.discount,
            payment.total_amount,
            payment.payment_status,
            payment.payment_type || "-",
            payment.pnr_number,
          ];

          csvContent += row.map((val) => `"${val}"`).join(",") + "\n";
        });

        filename = `payments-report-${getMonthName(
          selectedMonth,
        )}-${selectedYear}.csv`;
      } else if (type === "sales") {
        const headers = [
          "Booking Number",
          "Invoice Date",
          "Customer Name",
          "Vendor Name",
          "Airport",
          "Service",
          "Number Of Service",
          "Gross Total (incl discount %)",
          "Vendor Discount Amount",
          "Sub Total",
          "Discount",
          "Child Discount",
          "CGST",
          "SGST",
          "IGST",
          "Admin Earnings",
          "Sales Status",
          "Booking Type",
        ];

        csvContent += headers.join(",") + "\n";

        exportData.forEach((sale) => {
          const row = [
            sale.booking_serial_no,
            sale.invoice_date,
            sale.customer_name,
            sale.vendor_name,
            sale.name,
            sale.service,
            sale.numberOfService,
            sale.gross_total,
            sale.vendor_commission,
            sale.sub_total,
            sale.discount,
            sale.child_discount,
            sale.cgst,
            sale.sgst,
            sale.igst,
            sale.admin_earnings,
            sale.sales_status,
            sale.booking_type,
          ];

          csvContent += row.map((val) => `"${val}"`).join(",") + "\n";
        });

        const timePeriod =
          timePeriodTab === 0
            ? `day-${selectedDay}-`
            : timePeriodTab === 1
              ? `month-${getMonthName(selectedMonth)}-`
              : "year-";
        filename = `sales-report-${timePeriod}${selectedYear}.csv`;
      } else if (type === "bookings") {
        const headers = [
          "Booking Number",
          "Booking Serial No",
          "Date of Booking",
          "Booked User Name",
          "Booked For",
          "PNR Number",
          "Airport Name",
          "Airport Type",
          "First Name",
          "Last Name",
          "Company Name",
          "Number of Adults",
          "Number of Child",
          "Number of Infant",
          "Total Passenger",
          "Child Discount",
          "Service",
          "Service Type Label",
          "Notes",
          "DateTime of Arrival",
          "DateTime of Departure",
          "Arrival City",
          "Arrival Airline",
          "Arrival Flight Number",
          "Departure City",
          "Departure Airline",
          "Departure Flight Number",
          "Payment Status",
          "Status",
          "Arrival Flight",
          "Departure Flight",
          "Booked Type",
        ];

        csvContent += headers.join(",") + "\n";

        exportData.forEach((booking) => {
          const row = [
            booking.booking_number,
            booking.booking_serial_no,
            booking.date_of_booking,
            booking.booked_user_name,
            booking.booked_for,
            booking.pnr_number,
            booking.airport_name,
            booking.airport_type,
            booking.first_name,
            booking.last_name,
            booking.company_name,
            booking.number_of_adults,
            booking.number_of_child,
            booking.number_of_infant,
            booking.total_passenger,
            booking.child_discount,
            booking.service,
            booking.service_type_label,
            booking.notes,
            booking.datetime_of_arrival,
            booking.datetime_of_departure,
            booking.arrival_city,
            booking.arrival_airline,
            booking.arrival_flight_number,
            booking.departure_city,
            booking.departure_airline,
            booking.departure_flight_number,
            booking.payment_status,
            booking.status,
            booking.arrival_flight,
            booking.departure_flight,
            booking.booked_type,
          ];

          csvContent += row.map((val) => `"${val}"`).join(",") + "\n";
        });

        filename = `bookings-report-${getMonthName(
          selectedMonth,
        )}-${selectedYear}.csv`;
      }

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, filename);
      toast.success("Export completed successfully!");
      setExportLoading(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed. Please try again.");
      setExportLoading(false);
    }
  };

  // Remove the makeExportApiCall function as it's no longer needed

  if (loading) {
    return (
      <Box textAlign="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={5} color="error.main">
        <p>Error loading data: {error}</p>
      </Box>
    );
  }

  const totalCount =
    type === "sales" || type === "bookings" || type === "payments"
      ? bookings?.totalRecords || 0
      : filteredData.length;

  return (
    <div className="report-container">
      <div className="report-header">
        <h2>{type.charAt(0).toUpperCase() + type.slice(1)} Report</h2>
        <div className="report-filters">
          <TextField
            label="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ width: { xs: "100%", sm: "250px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {((type === "sales" && timePeriodTab === 1) ||
            type === "payments") && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                label="Month"
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarMonth fontSize="small" />
                  </InputAdornment>
                }
              >
                {Array.from({ length: 12 }).map((_, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {getMonthName(index + 1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Day selector only for sales daily tab */}
          {type === "sales" && timePeriodTab === 0 && (
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Day</InputLabel>
              <Select
                value={selectedDay}
                onChange={handleDayChange}
                label="Day"
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarToday fontSize="small" />
                  </InputAdornment>
                }
              >
                {Array.from({
                  length: getDaysInMonth(selectedMonth, selectedYear),
                }).map((_, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {index + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <DynamicYearSelector
            selectedYear={selectedYear}
            setSelectedYear={handleYearChange}
          />

          <Button
            startIcon={
              exportLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Download />
              )
            }
            variant="contained"
            className="orange-button"
            onClick={handleExport}
            size="medium"
            disabled={exportLoading}
          >
            {exportLoading ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>

      {/* Time Period Tabs for Sales Reports */}
      {type === "sales" && (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={timePeriodTab}
              onChange={handleTimePeriodChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab
                icon={<CalendarToday />}
                label="Daily"
                iconPosition="start"
              />
              <Tab
                icon={<CalendarViewWeek />}
                label="Monthly"
                iconPosition="start"
              />
              <Tab
                icon={<CalendarViewMonth />}
                label="Yearly"
                iconPosition="start"
              />
            </Tabs>
          </Paper>

          {/* Revenue Metrics Dashboard */}
          <Grid container justifyContent="center" spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    color="primary"
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <TrendingUp sx={{ mr: 1 }} />
                    Total Revenues
                  </Typography>
                  <Typography variant="h4" component="div">
                    ₹{getCurrentMetrics().totalRevenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {timePeriodTab === 0
                      ? `Day ${selectedDay}`
                      : timePeriodTab === 1
                        ? getMonthName(selectedMonth)
                        : selectedYear}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    color="success.main"
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <MoneyOffCsredTwoTone sx={{ mr: 1 }} />
                    Earnings
                  </Typography>
                  <Typography variant="h4" component="div">
                    ₹{getCurrentMetrics().adminEarnings.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {timePeriodTab === 0
                      ? `Day ${selectedDay}`
                      : timePeriodTab === 1
                        ? getMonthName(selectedMonth)
                        : selectedYear}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    color="info.main"
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <MoneyOffCsredTwoTone sx={{ mr: 1 }} />
                    Vendor Amount
                  </Typography>
                  <Typography variant="h4" component="div">
                    ₹{getCurrentMetrics().vendorAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {timePeriodTab === 0
                      ? `Day ${selectedDay}`
                      : timePeriodTab === 1
                        ? getMonthName(selectedMonth)
                        : selectedYear}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    color="warning.main"
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <DateRange sx={{ mr: 1 }} />
                    Total Bookings
                  </Typography>
                  <Typography variant="h4" component="div">
                    {getCurrentMetrics().totalBookings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {timePeriodTab === 0
                      ? `Day ${selectedDay}`
                      : timePeriodTab === 1
                        ? getMonthName(selectedMonth)
                        : selectedYear}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    color="primary"
                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                  >
                    <Person2 sx={{ mr: 1 }} />
                    Total Services
                  </Typography>
                  <Typography variant="h4" component="div">
                    {getCurrentMetrics().totalServices}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {timePeriodTab === 0
                      ? `Day ${selectedDay}`
                      : timePeriodTab === 1
                        ? getMonthName(selectedMonth)
                        : selectedYear}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <div className="report-table-container">
        <table>
          <thead>
            <tr>
              {type === "payments" && (
                <>
                  {renderSortableHeader("Booking Number", "booking_serial_no")}
                  {renderSortableHeader("Transaction ID", "transaction_id")}
                  {renderSortableHeader("Payment Date", "payment_date")}
                  {renderSortableHeader("Customer Name", "customer_name")}
                  {renderSortableHeader("Service", "service")}
                  {renderSortableHeader("Service Date", "service_date")}
                  {renderSortableHeader(
                    "Gross Total (incl discount %)",
                    "gross_total",
                  )}
                  {renderSortableHeader(
                    "Vendor Discount Amount",
                    "vendor_discount",
                  )}
                  {renderSortableHeader("Sub Total", "sub_total")}
                  {renderSortableHeader("CGST", "cgst")}
                  {renderSortableHeader("SGST", "sgst")}
                  {renderSortableHeader("Discount", "discount")}
                  {renderSortableHeader("Total Amount", "total_amount")}
                  {renderSortableHeader("Payment Status", "payment_status")}
                  {renderSortableHeader("PNR Number", "pnr_number")}
                </>
              )}
              {type === "sales" && (
                <>
                  {renderSortableHeader("Booking Number", "booking_serial_no")}
                  {renderSortableHeader("Invoice Date", "invoice_date")}
                  {renderSortableHeader("Customer Name", "customer_name")}
                  {renderSortableHeader("Vendor Name", "vendor_name")}
                  {renderSortableHeader("Airport", "name")}
                  {renderSortableHeader("Service", "service")}
                  {renderSortableHeader("Number Of Service", "numberOfService")}
                  {renderSortableHeader(
                    "Gross Total (incl discount %)",
                    "gross_total",
                  )}
                  {renderSortableHeader(
                    "Vendor Discount Amount",
                    "vendor_commission",
                  )}
                  {renderSortableHeader("Sub Total", "sub_total")}
                  {renderSortableHeader("Discount", "discount")}
                  {renderSortableHeader("Child Discount", "child_discount")}
                  {renderSortableHeader("CGST", "cgst")}
                  {renderSortableHeader("SGST", "sgst")}
                  {renderSortableHeader("IGST", "igst")}
                  {renderSortableHeader("Admin Earnings", "admin_earnings")}
                  {renderSortableHeader("Payment Status", "sales_status")}
                  {renderSortableHeader("Booking Type", "booking_type")}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr key={index}>
                  {type === "payments" && (
                    <>
                      <td>{item.booking_serial_no}</td>
                      <td>{item.transaction_id}</td>
                      <td>{item.payment_date}</td>
                      <td>{item.customer_name}</td>
                      <td>{item.service}</td>
                      <td>{item.service_date}</td>
                      <td>{item.gross_total}</td>
                      <td>{item.vendor_discount}</td>
                      <td>{item.sub_total}</td>
                      <td>{item.cgst}</td>
                      <td>{item.sgst}</td>
                      <td>{item.discount}</td>
                      <td>{item.total_amount}</td>
                      <td>{item.payment_status}</td>
                      <td>{item.pnr_number}</td>
                    </>
                  )}
                  {type === "sales" && (
                    <>
                      <td>{item.booking_serial_no}</td>
                      <td>{item.invoice_date}</td>
                      <td>{item.customer_name}</td>
                      <td>{item.vendor_name}</td>
                      <td>{item.name}</td>
                      <td>{item.service}</td>
                      <td>{item.numberOfService}</td>
                      <td>{item.gross_total}</td>
                      <td>{item.vendor_commission}</td>
                      <td>{item.sub_total}</td>
                      <td>{item.discount}</td>
                      <td>{item.child_discount}</td>
                      <td>{item.cgst}</td>
                      <td>{item.sgst}</td>
                      <td>{item.igst}</td>
                      <td>{item.admin_earnings}</td>
                      <td>{item.sales_status}</td>
                      <td>{item.booking_type}</td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={
                    type === "customers"
                      ? 5
                      : type === "vendors"
                        ? 9
                        : type === "bookings"
                          ? 15
                          : type === "payments"
                            ? 15
                            : type === "sales"
                              ? 18
                              : 0
                  }
                  style={{ textAlign: "center" }}
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default ReportTable;
